import * as React from "react";
import { twMerge } from "tailwind-merge";
import Text from "./Text";

export type CardAdvanceRowProps = {
  icon?: string;
  label?: string;
  value?: React.ReactNode;
  extra?: React.ReactNode;
};

export type CardAdvanceItem = CardAdvanceRowProps | React.ReactElement;

export type CardAdvanceModel = {
  title?: string;
  subtitle?: React.ReactNode;
  titleEnd?: React.ReactNode;
  items: CardAdvanceItem[];
  footer?: React.ReactNode;
};

type DefaultCardProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType;
  type?: undefined;
  children: React.ReactNode;
};

type AdvanceCardProps = Omit<React.HTMLAttributes<HTMLDivElement>, "title"> &
  CardAdvanceModel & {
    as?: React.ElementType;
    type: "advance";
  };

export type CardProps = DefaultCardProps | AdvanceCardProps;

export function CardAdvanceRow({
  icon,
  label,
  value,
  extra,
}: CardAdvanceRowProps) {
  return (
    <div className="flex items-center justify-between gap-[12px]">
      <div className="flex min-w-0 items-center gap-[12px]">
        {!!icon && (
          <div className="w-[30px] h-[30px] shrink-0 flex items-center justify-center rounded-full bg-[#3398F4]">
            <img
              src={icon}
              alt={label}
              className="w-[18px] h-[18px] object-contain"
            />
          </div>
        )}
        <div className="min-w-0">
          {!!label && <Text level={3}>{label}</Text>}
          {value != null &&
            value !== "" &&
            (typeof value === "string" || typeof value === "number" ? (
              <Text>{value}</Text>
            ) : (
              value
            ))}
        </div>
      </div>
      {extra != null &&
        extra !== "" &&
        (typeof extra === "string" || typeof extra === "number" ? (
          <Text>{extra}</Text>
        ) : (
          extra
        ))}
    </div>
  );
}

function CardAdvance({
  as,
  className,
  title,
  subtitle,
  titleEnd,
  items,
  footer,
  ...rest
}: AdvanceCardProps) {
  const Comp = (as ?? "section") as React.ElementType;
  const hasHeader = Boolean(subtitle || titleEnd);
  return (
    <div>
      {title && !hasHeader && (
        <Text level={4} className="font-[700] mb-[24px]">
          {title}
        </Text>
      )}
      <Comp
        className={twMerge(
          "rounded-[8px] p-[0px] overflow-hidden border border-divider bg-surface",
          className,
        )}
        {...rest}
      >
        {hasHeader && (
          <div className="p-[16px] bg-table-header border-b border-divider">
            {titleEnd ? (
              <div className="flex flex-wrap items-center justify-between gap-[12px]">
                <Text level={1}>{title}</Text>
                {titleEnd}
              </div>
            ) : (
              <Text level={1}>{title}</Text>
            )}
            {subtitle}
          </div>
        )}
        {!!items.length && (
          <div className="p-[16px] grid gap-[16px]">
            {items.map((item, index) =>
              React.isValidElement(item) ? (
                <React.Fragment key={index}>{item}</React.Fragment>
              ) : (
                <CardAdvanceRow key={index} {...item} />
              ),
            )}
          </div>
        )}
        {footer}
      </Comp>
    </div>
  );
}

function Card(props: CardProps) {
  if (props.type === "advance") return <CardAdvance {...props} />;
  const { as, className, children, ...rest } = props;
  const Comp = (as ?? "section") as React.ElementType;
  return (
    <Comp
      className={twMerge("rounded-[8px] p-[20px] bg-surface", className)}
      {...rest}
    >
      {children}
    </Comp>
  );
}

Card.AdvanceRow = CardAdvanceRow;

export default Card;
